/**
 * `graph_view` block (spec §7.4.7, §15.8): query text, layout, pinned positions and style
 * overrides live in Loro; results are fetched live (debounced on query changes, so another
 * user's edit re-renders here) and cached in IndexedDB for offline viewing with a stale marker.
 */
import { useParams } from "@tanstack/react-router";
import { LoroMap, LoroText } from "loro-crdt";
import { ChevronDown, ChevronRight, Play } from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePrefixes, useStaleGraphs, type QueryResult } from "../../api/hooks";
import type { LayoutName, Positions } from "../../graph/CytoscapeView";
import { GraphCanvas } from "../../graph/GraphCanvas";
import { ResultTable } from "../../graph/ResultTable";
import { mountSparqlEditor } from "../../graph/SparqlEditor";
import type { StyleOverrides } from "../../graph/stylesheet";
import { queryCacheKey, runWithCache } from "../../graph/queryCache";
import { useGraphExploration } from "../../graph/useGraphExploration";
import { useLoroSelector } from "../../sync/hooks";
import type { BlockComponentProps } from "../registry";
import { LoroTextInput, bodyMap, ensureText } from "../textField";
import { useT } from "../../i18n";
import { reconcileBuilderState } from "../../graph/builder/state";

// [M34] Sparnatural loads only when a reader first opens the builder (rule 6)
const QueryBuilder = lazy(() => import("../../graph/builder/QueryBuilder").then((m) => ({ default: m.QueryBuilder })));

const sameJson = <T,>(a: T, b: T) => JSON.stringify(a) === JSON.stringify(b);

function readMap<T>(body: LoroMap | null, key: string): Record<string, T> {
  const m = body?.get(key);
  return m instanceof LoroMap ? (m.toJSON() as Record<string, T>) : {};
}

export function GraphViewBlock({ handle, treeId, node, readOnly, onFocus }: BlockComponentProps) {
  const t = useT();
  const { workspaceId } = useParams({ strict: false }) as { workspaceId: string };
  const body = bodyMap(node);
  const prefixes = usePrefixes();
  const prefixesRef = useRef<Record<string, string>>({});
  const host = useRef<HTMLDivElement>(null);
  const query = useLoroSelector(handle.doc, () => {
    const t = body?.get("query");
    return t instanceof LoroText ? t.toString() : "";
  }) ?? "";
  const layout = (useLoroSelector(handle.doc, () => String(body?.get("layout") ?? "fcose")) ?? "fcose") as LayoutName;
  const positions = useLoroSelector(handle.doc, () => readMap<{ x: number; y: number }>(body, "positions"), sameJson) ?? {};
  const overrides = useLoroSelector(handle.doc, () => readMap<Record<string, unknown>>(body, "styleOverrides"), sameJson) ?? {};
  const [editorOpen, setEditorOpen] = useState(() => !query.trim());
  const [run, setRun] = useState<{ result: QueryResult | null; stale?: number; error?: string; running: boolean }>({ result: null, running: false });
  const [selected, setSelected] = useState<string | null>(null);
  // [M34] the visual query builder in a dialog; its state stays in this tab (spec §15.10, Q95)
  const [building, setBuilding] = useState(false);
  const builderKey = `blk:${handle.crdtDocId}:${treeId}`;
  const useBuiltQuery = (q: string) => {
    if (!body) return;
    const text = ensureText(body, "query");
    text.delete(0, text.length);
    text.insert(0, q);
    handle.doc.commit();
    setBuilding(false);
    setEditorOpen(true);
  };

  useEffect(() => {
    prefixesRef.current = prefixes.data ?? {};
  }, [prefixes.data]);

  const execute = useCallback(
    async (q: string) => {
      if (!q.trim()) return;
      setRun((r) => ({ ...r, running: true }));
      try {
        // cache key per spec §15.8: (crdtDocId, treeId, sha256(query))
        const res = await runWithCache(workspaceId, q, await queryCacheKey(handle.crdtDocId, treeId, q));
        setRun({ result: res.result, stale: res.stale ? res.fetchedAt : undefined, error: res.error, running: false });
      } catch (e) {
        setRun((r) => ({ ...r, running: false, error: e instanceof Error ? e.message : String(e) }));
      }
    },
    [workspaceId, handle.crdtDocId, treeId],
  );
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  }, [execute]);

  // live results: re-run when the (shared) query text changes
  useEffect(() => {
    const t = setTimeout(() => void executeRef.current(query), 700);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const el = host.current;
    if (!editorOpen || !el || !body) return;
    const text = ensureText(body, "query");
    const h = mountSparqlEditor(el, {
      doc: handle.doc,
      text,
      readOnly,
      workspaceId,
      prefixes: () => prefixesRef.current,
      onRun: (q) => void executeRef.current(q),
    });
    return () => h.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen, handle.doc, node.id, readOnly, workspaceId]);

  const staleGraphs = useStaleGraphs(workspaceId);
  const { graph, info, expand, incremental } = useGraphExploration(workspaceId, run.result, { mergeDerived: true, staleGraphs: staleGraphs.data });
  const selectedLabel = selected ? (info.get(selected)?.label ?? selected) : null;
  const showTable = !!run.result && (!graph || graph.tableOnly || graph.tooLarge);

  const onDragEnd = useCallback(
    (iri: string, pos: { x: number; y: number }) => {
      if (readOnly || !body) return;
      const existing = body.get("positions");
      const map = existing instanceof LoroMap ? existing : body.setContainer("positions", new LoroMap());
      map.set(iri, pos);
      handle.doc.commit();
    },
    [readOnly, body, handle.doc],
  );

  const stableOverrides = useMemo(() => overrides as StyleOverrides, [overrides]);
  const stablePositions = useMemo(() => positions as Positions, [positions]);

  if (!body) return null;
  return (
    <div className="rounded-md border border-neutral-200 bg-[var(--surface)] shadow-[var(--shadow-sm)] dark:border-neutral-800" data-testid="graph-view-block" data-fill-column onFocus={onFocus}>
      <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200 px-2 py-1 text-xs dark:border-neutral-800">
        <button onClick={() => setEditorOpen((v) => !v)} aria-label={t("graph.toggleEditor")} data-testid="graph-view-toggle">
          {editorOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <LoroTextInput handle={handle} map={body} field="title" placeholder={t("graph.blockTitle")} readOnly={readOnly} className="min-w-0 grow bg-transparent text-sm font-medium outline-none" testId="graph-view-title" />
        <select
          className="rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700"
          value={layout}
          disabled={readOnly}
          data-testid="graph-view-layout"
          onChange={(e) => {
            body.set("layout", e.target.value); // LWW, watched key
            handle.doc.commit();
          }}
        >
          <option value="fcose">fcose</option>
          <option value="elk-layered">{t("graph.layout.elk")}</option>
          <option value="preset">{t("graph.layout.preset")}</option>
        </select>
        {!readOnly && (
          <button
            className="underline"
            onClick={() => {
              reconcileBuilderState(builderKey, query); // a hand edit since the last build detaches it
              setBuilding(true);
            }}
            data-testid="graph-view-build"
          >
            {t("graph.builder.openBlock")}
          </button>
        )}
        <button className="flex items-center gap-1" onClick={() => void execute(query)} disabled={run.running} data-testid="graph-view-run">
          <Play size={12} /> {t("graph.run")}
        </button>
        {run.running && <span>{t("graph.running")}</span>}
        {run.result && !run.running && (
          <span data-testid="graph-view-status">
            {t("graph.rows", { count: run.result.rowCount })}{graph && !graph.tableOnly ? ` · ${t("graph.nodes", { count: graph.nodes.length })}` : ""}
          </span>
        )}
        {run.stale && (
          <span className="rounded bg-amber-100 px-1 text-amber-900" data-testid="graph-stale">
            {t("graph.staleSince", { time: new Date(run.stale).toLocaleString() })}
          </span>
        )}
        {run.error && !run.stale && <span className="text-red-600" data-testid="graph-view-error">{run.error.slice(0, 160)}</span>}
      </div>
      {editorOpen && <div ref={host} className="max-h-48 overflow-auto border-b border-neutral-200 dark:border-neutral-800" data-testid="graph-view-editor" />}
      <div className="h-80" data-fill-surface>
        {showTable && run.result ? (
          <ResultTable result={run.result} />
        ) : (
          <GraphCanvas graph={graph} info={info} layout={layout} positions={stablePositions} overrides={stableOverrides} incremental={incremental} onSelect={setSelected} onExpand={(id) => void expand(id)} onDragEnd={onDragEnd} />
        )}
      </div>
      {selectedLabel && <div className="truncate border-t border-neutral-200 px-2 py-0.5 text-xs dark:border-neutral-800" data-testid="graph-view-selected">{selectedLabel}</div>}
      {building && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-[rgba(28,27,26,0.36)] p-8" onClick={() => setBuilding(false)}>
          <div
            className="w-full max-w-4xl rounded-lg border border-neutral-200 bg-[var(--surface)] shadow-[var(--shadow-lg)] dark:border-neutral-700"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t("graph.builder.open")}
            data-testid="graph-view-builder"
          >
            <Suspense fallback={null}>
              <QueryBuilder workspaceId={workspaceId} stateKey={builderKey} onUse={useBuiltQuery} />
            </Suspense>
          </div>
        </div>
      )}
    </div>
  );
}
